import { create } from "zustand";
import api from "../../utils/api";
import { shouldRetryDoorCommand } from "./doorBusyRetry";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AccessCard {
  id: string;
  card_number: string;
  type: "card" | "tag";
  company_id: string;
  // Patios this card grants access to. A card may be assigned to multiple
  // patios (mirroring a vehicle's yard_ids). `yard_id` is the legacy single
  // value kept for backward compatibility with older records/responses.
  yard_ids?: string[];
  yard_id?: string | null;
  vehicle_id: string | null;
  vehicle_plate: string | null;
  driver_id: string | null;
  driver_name: string | null;
  label: string;
  status: "active" | "inactive" | "lost" | "expired";
  valid_from: string | null;
  valid_until: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface YardAccessEvent {
  id: string;
  company_id: string;
  yard_id: string;
  yard_name: string;
  card_number: string;
  vehicle_id: string | null;
  vehicle_plate: string | null;
  driver_id: string | null;
  driver_name: string | null;
  event_type: "entry" | "exit";
  event_result: "granted" | "denied" | "unknown";
  source: "hikvision" | "manual" | "qr" | "sync";
  hikvision_event: HikvisionEventData | null;
  inspection_id?: string | null;
  notes?: string | null;
  timestamp: string;
  createdAt: string;
  /**
   * Which lane the controller reported, i.e. the raw reading behind
   * `event_type`. Null on manual rows and on devices with no reader map.
   */
  reader_direction?: "entry" | "exit" | null;
  /** Read twice in the same direction with nothing in between — worth a human look. */
  suspicious?: boolean;
  /**
   * "repeat_read" is the only code written now. "presence_inverted" is legacy:
   * rows stored while the backend still inferred direction from where the
   * vehicle had been, kept until the migration rewrites them.
   */
  suspicious_code?: "repeat_read" | "presence_inverted" | null;
  /** Operator-facing explanation, rendered verbatim in the detail modal. */
  suspicious_reason?: string | null;
  /**
   * El tag del evento es uno de los dos tags especiales de SU patio
   * (`truck_yards/{yard_id}.special_tags`). Lo sella el backend en la ingesta;
   * ausente o `null` en todo evento anterior y en cualquier tag normal. Qué
   * cambia en pantalla lo decide `specialTagCheckMode` (specialTags.ts).
   */
  special_tag?: "provider" | "untagged" | null;
  /**
   * Quién completó el registro manual de una pipa sin tag desde Inspecciones
   * (`PUT /yardInspection/registration`). `at` es un Timestamp de Firestore y
   * puede llegar serializado como `{_seconds}`: no se usa como fecha sin
   * normalizar. Ausente mientras el registro no se hizo.
   */
  manual_registration?: {
    by_id: string | null;
    by_name: string | null;
    at: unknown;
  } | null;
}

export interface HikvisionEventData {
  device_serial: string | null;
  device_name: string | null;
  major: number;
  minor: number;
  door_no: number;
  employee_no: string | null;
  verify_mode: string | null;
  event_id: string | null;
}

export type HikvisionConnectionMode = "hikgateway" | "direct" | "relay";

// A physical reader (lectora) wired to a door. A door typically has an entry
// reader and an exit reader (per the controller wiring diagram). `reader_no`
// is the controller's reader index (in=1, out=2 on Hikvision panels).
export interface HikvisionReader {
  reader_no: number;
  name: string;
  direction: "entry" | "exit";
}

/**
 * Un instante tal como LLEGA por el cable, que no siempre es un string ISO.
 *
 * `getHikvisionDeviceList` devuelve `doc.data()` sin serializar, y los campos
 * escritos con `FieldValue.serverTimestamp()` viajan como
 * `{_seconds, _nanoseconds}`: el Timestamp de `@google-cloud/firestore` no
 * define `toJSON()`, así que `JSON.stringify` vuelca sus campos internos.
 *
 * Esto estaba declarado como `string | null`, y el tipo mentía. TypeScript no
 * podía avisar porque nadie estrechaba el valor: la pantalla de relays hacía
 * `new Date(v)` derecho y pintaba "hace NaN d" en producción. Declararlo como
 * lo que realmente es obliga a normalizar antes de usarlo, que es el único
 * lugar donde el compilador puede atajar la próxima pantalla que lo consuma.
 */
export type FirestoreInstant =
  | string
  | { _seconds: number; _nanoseconds?: number }
  | null;

/**
 * Normaliza un `FirestoreInstant` a ISO, o `null` si no representa un instante.
 *
 * Vive acá, al lado del tipo, porque el que consume el tipo necesita esto:
 * dejarlo en una pantalla garantiza que la siguiente lo vuelva a escribir mal.
 * Devolver `null` —y no una fecha inventada— es lo que permite que quien llama
 * muestre "sin registro" en vez de propagar un `NaN` que termina pintado como
 * un dato.
 */
export function firestoreInstantToIso(v: FirestoreInstant | undefined): string | null {
  if (!v) return null;
  if (typeof v === "string") return Number.isNaN(Date.parse(v)) ? null : v;
  if (typeof v === "object" && typeof v._seconds === "number") {
    return new Date(v._seconds * 1000).toISOString();
  }
  return null;
}

export interface HikvisionDevice {
  id: string;
  device_serial: string;
  device_name: string;
  company_id: string;
  yard_id: string;
  yard_name: string | null;
  door_no: number;
  door_type: "entry" | "exit" | "bidirectional";
  // Physical readers on this door (entrada/salida). Optional: legacy door docs
  // created before the console→doors→readers model won't have it.
  readers?: HikvisionReader[] | null;
  // Controller model for the console header (e.g. "DS-K2812"). Doors sharing a
  // device_serial belong to the same physical controller/console.
  controller_model?: string | null;
  hikgateway_url: string | null;
  ip_address: string | null;
  connection_mode: HikvisionConnectionMode;
  username: string | null;
  // La API ya no devuelve la contraseña del controlador (sólo `has_password`);
  // se manda al crear o al cambiarla. Vacía al editar = sin cambios.
  password?: string | null;
  has_password?: boolean;
  device_port: number | null;
  webhook_target_url?: string | null;
  webhook_configured_at?: FirestoreInstant;
  last_probe_at?: FirestoreInstant;
  status: "online" | "offline";
  last_event_at: FirestoreInstant;
  // Heartbeat-driven liveness (relay-mode controllers). Updated by the cloud
  // webhook each time the relay agent reports in (~30s), and forced to
  // "disconnected" by a scheduled function when beats go silent (>90s).
  last_heartbeat_at?: FirestoreInstant;
  connection_status?: "connected" | "disconnected" | null;
  connection_lost_at?: FirestoreInstant;
  // Mismo caso que los tres de arriba: `hikvisionAccessController` los escribe
  // con `FieldValue.serverTimestamp()` y este endpoint los devuelve crudos.
  // Estaban tipados `string` y se renderizaban con `new Date(...)` derecho, o
  // sea "Invalid Date" en el modal de detalle de TODOS los dispositivos.
  createdAt: FirestoreInstant;
  updatedAt: FirestoreInstant;
}

export interface HikvisionDeviceProbeResult {
  deviceName?: string | null;
  model?: string | null;
  serialNumber?: string | null;
  macAddress?: string | null;
  firmwareVersion?: string | null;
}

export type HikvisionDoorAction = "open" | "close" | "alwaysOpen" | "alwaysClose";

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// ─── Store ────────────────────────────────────────────────────────────────────

interface HikvisionAccessStoreState {
  // Access Events
  accessEvents: YardAccessEvent[];
  accessEventsPagination: Pagination | null;
  selectedEvent: YardAccessEvent | null;
  eventsLoading: boolean;
  setAccessEvents: (events: YardAccessEvent[]) => void;
  setSelectedEvent: (event: YardAccessEvent | null) => void;
  setEventsLoading: (loading: boolean) => void;

  // Access Cards
  accessCards: AccessCard[];
  accessCardsPagination: Pagination | null;
  cardsLoading: boolean;
  setAccessCards: (cards: AccessCard[]) => void;
  setCardsLoading: (loading: boolean) => void;
  fetchCards: (params?: FetchAccessCardsParams) => Promise<void>;
  createCard: (data: CreateAccessCardParams) => Promise<AccessCard | null>;
  updateCard: (data: UpdateAccessCardParams) => Promise<AccessCard | null>;
  removeCard: (id: string) => Promise<boolean>;

  // Hikvision Devices
  hikvisionDevices: HikvisionDevice[];
  devicesPagination: Pagination | null;
  devicesLoading: boolean;
  setHikvisionDevices: (devices: HikvisionDevice[]) => void;
  setDevicesLoading: (loading: boolean) => void;
}

export const useHikvisionAccessStore = create<HikvisionAccessStoreState>(
  (set) => ({
    // Access Events
    accessEvents: [],
    accessEventsPagination: null,
    selectedEvent: null,
    eventsLoading: false,
    setAccessEvents: (events) => set({ accessEvents: events }),
    setSelectedEvent: (event) => set({ selectedEvent: event }),
    setEventsLoading: (loading) => set({ eventsLoading: loading }),

    // Access Cards
    accessCards: [],
    accessCardsPagination: null,
    cardsLoading: false,
    setAccessCards: (cards) => set({ accessCards: cards }),
    setCardsLoading: (loading) => set({ cardsLoading: loading }),

    fetchCards: async (params = {}) => {
      set({ cardsLoading: true });
      try {
        const result = await fetchAccessCards(params);
        set({
          accessCards: result.data,
          accessCardsPagination: result.pagination,
          cardsLoading: false,
        });
      } catch {
        set({ cardsLoading: false });
      }
    },

    createCard: async (data) => {
      try {
        const card = await createAccessCard(data);
        set((s) => ({ accessCards: [card, ...s.accessCards] }));
        return card;
      } catch {
        return null;
      }
    },

    updateCard: async (data) => {
      try {
        const card = await updateAccessCard(data);
        set((s) => ({
          accessCards: s.accessCards.map((c) =>
            c.id === data.id ? { ...c, ...card } : c
          ),
        }));
        return card;
      } catch {
        return null;
      }
    },

    removeCard: async (id) => {
      try {
        await deleteAccessCard(id);
        set((s) => ({
          accessCards: s.accessCards.filter((c) => c.id !== id),
        }));
        return true;
      } catch {
        return false;
      }
    },

    // Hikvision Devices
    hikvisionDevices: [],
    devicesPagination: null,
    devicesLoading: false,
    setHikvisionDevices: (devices) => set({ hikvisionDevices: devices }),
    setDevicesLoading: (loading) => set({ devicesLoading: loading }),
  })
);

// ─── API Functions — Yard Access Events ───────────────────────────────────────

export interface FetchAccessEventsParams {
  yard_id?: string;
  /**
   * Varios patios separados por coma (filtro múltiple de Accesos). El backend
   * lo ignora si también viene `yard_id`, que manda.
   */
  yard_ids?: string;
  vehicle_id?: string;
  card_number?: string;
  event_type?: "entry" | "exit";
  event_result?: "granted" | "denied" | "unknown";
  date_from?: string;
  date_to?: string;
  limit?: number;
  page?: number;
}

export const fetchYardAccessEvents = async (
  params: FetchAccessEventsParams = {}
): Promise<{ data: YardAccessEvent[]; pagination: Pagination }> => {
  const res = await api.get("/yardAccessEventList", { params });
  return res.data;
};

export const fetchYardAccessEvent = async (
  id: string
): Promise<YardAccessEvent> => {
  const res = await api.get("/yardAccessEvent", { params: { id } });
  return res.data.data;
};

export interface CreateManualAccessEventParams {
  yard_id: string;
  event_type: "entry" | "exit";
  vehicle_id?: string;
  vehicle_plate?: string;
  driver_name?: string;
  card_number?: string;
  notes?: string;
  // "Nuevo Evento Manual" del historial: entrada como si hubieran pasado el tag
  // de pipas sin tag del patio. El backend pone el número desde la
  // configuración del patio y no exige unidad (la captura el paso 1).
  special_tag_kind?: "untagged";
}

export const createManualAccessEvent = async (
  data: CreateManualAccessEventParams
): Promise<YardAccessEvent> => {
  const res = await api.post("/yardAccessEvent", data);
  return res.data.data;
};

// ─── API Functions — Access Cards ─────────────────────────────────────────────

export interface FetchAccessCardsParams {
  yard_id?: string;
  vehicle_id?: string;
  type?: "card" | "tag";
  status?: "active" | "inactive" | "lost" | "expired";
  limit?: number;
  page?: number;
}

export const fetchAccessCards = async (
  params: FetchAccessCardsParams = {}
): Promise<{ data: AccessCard[]; pagination: Pagination }> => {
  const res = await api.get("/accessCardList", { params });
  return res.data;
};

export const fetchAccessCard = async (id: string): Promise<AccessCard> => {
  const res = await api.get("/accessCard", { params: { id } });
  return res.data.data;
};

export interface CreateAccessCardParams {
  card_number: string;
  label: string;
  type?: "card" | "tag";
  yard_ids?: string[];
  yard_id?: string | null;
  vehicle_id?: string | null;
  vehicle_plate?: string | null;
  driver_id?: string | null;
  driver_name?: string | null;
  status?: "active" | "inactive" | "lost" | "expired";
  valid_from?: string | null;
  valid_until?: string | null;
}

export const createAccessCard = async (
  data: CreateAccessCardParams
): Promise<AccessCard> => {
  const res = await api.post("/accessCard", data);
  return res.data.data;
};

export interface UpdateAccessCardParams extends Partial<CreateAccessCardParams> {
  id: string;
}

export const updateAccessCard = async (
  data: UpdateAccessCardParams
): Promise<AccessCard> => {
  const res = await api.put("/accessCard", data);
  return res.data.data;
};

export const deleteAccessCard = async (id: string): Promise<void> => {
  await api.delete("/accessCard", { params: { id } });
};

// ─── API Functions — Hikvision Devices ────────────────────────────────────────

export interface FetchHikvisionDevicesParams {
  yard_id?: string;
  status?: "online" | "offline";
  limit?: number;
  page?: number;
}

export const fetchHikvisionDevices = async (
  params: FetchHikvisionDevicesParams = {}
): Promise<{ data: HikvisionDevice[]; pagination: Pagination }> => {
  const res = await api.get("/hikvisionDeviceList", { params });
  return res.data;
};

export const fetchHikvisionDevice = async (
  id: string
): Promise<HikvisionDevice> => {
  const res = await api.get("/hikvisionDevice", { params: { id } });
  return res.data.data;
};

export interface CreateHikvisionDeviceParams {
  device_serial: string;
  device_name: string;
  yard_id: string;
  door_no: number;
  door_type: "entry" | "exit" | "bidirectional";
  // Physical readers wired to this door (reader_no → entry/exit). Direction
  // classifies each swipe by which reader fired; takes precedence over door_type.
  readers?: HikvisionReader[] | null;
  hikgateway_url?: string | null;
  ip_address?: string | null;
  connection_mode?: HikvisionConnectionMode;
  username?: string | null;
  password?: string | null;
  device_port?: number | null;
  status?: "online" | "offline";
}

export const createHikvisionDevice = async (
  data: CreateHikvisionDeviceParams
): Promise<HikvisionDevice> => {
  const res = await api.post("/hikvisionDevice", data);
  return res.data.data;
};

export interface UpdateHikvisionDeviceParams
  extends Partial<CreateHikvisionDeviceParams> {
  id: string;
}

export const updateHikvisionDevice = async (
  data: UpdateHikvisionDeviceParams
): Promise<HikvisionDevice> => {
  const res = await api.put("/hikvisionDevice", data);
  return res.data.data;
};

export const deleteHikvisionDevice = async (id: string): Promise<void> => {
  await api.delete("/hikvisionDevice", { params: { id } });
};

// ─── Direct ISAPI control (only for connection_mode = "direct") ───────────────

export interface TestHikvisionDeviceResult {
  success: boolean;
  data?: HikvisionDeviceProbeResult | null;
  error?: string;
}

export const testHikvisionDevice = async (
  id: string
): Promise<TestHikvisionDeviceResult> => {
  const res = await api.post("/hikvisionDevice/test", { id });
  return res.data;
};

export interface ControlHikvisionDeviceParams {
  id: string;
  action: HikvisionDoorAction;
  door_no?: number;
}

export interface ControlHikvisionDeviceResult {
  success: boolean;
  action: HikvisionDoorAction;
  error?: string;
}

export const controlHikvisionDevice = async (
  params: ControlHikvisionDeviceParams
): Promise<ControlHikvisionDeviceResult> => {
  const res = await api.post("/hikvisionDevice/control", params);
  return res.data;
};

export interface SetupHikvisionWebhookParams {
  id: string;
  webhook_url?: string;
}

export interface SetupHikvisionWebhookResult {
  success: boolean;
  webhook_url: string;
  error?: string;
}

export const setupHikvisionWebhook = async (
  params: SetupHikvisionWebhookParams
): Promise<SetupHikvisionWebhookResult> => {
  const res = await api.post("/hikvisionDevice/setupWebhook", params);
  return res.data;
};

// ─── Door Commands (remote open/close) ───────────────────────────────────────

export interface DoorCommand {
  id: string;
  company_id: string;
  device_id: string;
  device_name: string | null;
  controllerIp: string;
  doorNo: number;
  action: "open" | "close" | "keepOpen";
  status: "pending" | "in_progress" | "completed" | "failed";
  result?: { success: boolean; message: string } | null;
  requestedBy: string | null;
  createdAt: string;
  executedAt?: string | null;
}

export interface CreateDoorCommandParams {
  device_id: string;
  action: "open" | "close" | "keepOpen";
  // Puerta del controlador; sin ella el backend usa el `door_no` del documento.
  // Hace falta en un dispositivo bidireccional de dos puertas (Acuapue).
  door_no?: number;
}

export const sendDoorCommand = async (
  data: CreateDoorCommandParams
): Promise<DoorCommand> => {
  const res = await api.post("/doorCommand", data);
  return res.data.data;
};

export const getDoorCommandStatus = async (
  commandId: string
): Promise<DoorCommand> => {
  const res = await api.get("/doorCommandStatus", {
    params: { command_id: commandId },
  });
  return res.data.data;
};

/**
 * Send a door command and poll until it completes (or times out).
 * Returns the final command status.
 */
export const sendDoorCommandAndWait = async (
  data: CreateDoorCommandParams,
  timeoutMs = 15000,
  pollIntervalMs = 1000
): Promise<DoorCommand> => {
  const cmd = await sendDoorCommand(data);
  const deadline = Date.now() + timeoutMs;

  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, pollIntervalMs));
    const status = await getDoorCommandStatus(cmd.id);
    if (status.status === "completed" || status.status === "failed") {
      return status;
    }
  }

  // Timeout — return last known status
  return getDoorCommandStatus(cmd.id);
};

// ─── Controller History Requests (read the device's stored event log) ────────
//
// Cloud-routed: the front-end creates a request, the on-site relay polls for it,
// asks the local SDK bridge for the controller's stored events, and posts them
// back. This lets the records screen work from any machine — no bridge access
// needed from the browser. Read-only: nothing is written to yardAccessEvents.

export interface ControllerHistoryRecord {
  time: string;
  cardNo: string | null;
  major: number;
  minor: number;
  doorNo: number;
  cardReaderNo: number;
}

/** Which event type to pull from the controller's stored log. Mirrors the
 *  on-site bridge taxonomy (EventHistory.EventFilter) and the cloud whitelist:
 *   all      = everything still retained on the device
 *   access   = credential decisions only (granted OR denied card/biometric
 *              reads) — excludes manual door ops, alarms and door-status noise
 *   granted  = authorized CARD passes only
 *   denied   = rejected CARD passes only
 *   manual   = door open/close with no credential (exit-button, manual,
 *              remote/operator open) — not a card decision
 *   alarm    = tamper / door-forced / alarm events only
 *   other    = device/door-status events that are none of the above */
export type ControllerHistoryFilter =
  | "all"
  | "access"
  | "granted"
  | "denied"
  | "manual"
  | "alarm"
  | "other";

export interface ControllerHistoryRequest {
  id: string;
  company_id: string;
  device_id: string;
  device_name: string | null;
  controllerIp: string | null;
  from: string | null;
  to: string | null;
  max: number;
  filter?: ControllerHistoryFilter;
  status: "pending" | "in_progress" | "completed" | "failed";
  events: ControllerHistoryRecord[];
  count?: number;
  truncated?: boolean;
  // Serial of the controller the on-site bridge actually read. If it differs
  // from the requested device's serial, the request was answered by a different
  // controller (single-bridge site) and must not be synced.
  controllerSerial?: string | null;
  error?: string | null;
  createdAt: string;
  completedAt?: string | null;
}

export interface RequestControllerHistoryParams {
  device_id: string;
  from?: string | null;
  to?: string | null;
  max?: number;
  /** Event type to pull. See {@link ControllerHistoryFilter}. Defaults to "all"
   *  server-side. */
  filter?: ControllerHistoryFilter;
}

export const requestControllerHistory = async (
  data: RequestControllerHistoryParams,
): Promise<{ id: string }> => {
  const res = await api.post("/hikvisionHistoryRequest", data);
  return res.data.data;
};

export const getControllerHistoryStatus = async (
  id: string,
): Promise<ControllerHistoryRequest> => {
  const res = await api.get("/hikvisionHistoryRequestStatus", {
    params: { id },
  });
  return res.data.data;
};

/**
 * Create a history request and poll until the relay fulfils it (or times out).
 * The default timeout is generous because a wide date range can take a while
 * for the controller to return over the SDK.
 */
export const fetchControllerHistoryAndWait = async (
  data: RequestControllerHistoryParams,
  timeoutMs = 90000,
  pollIntervalMs = 2000,
): Promise<ControllerHistoryRequest> => {
  const { id } = await requestControllerHistory(data);
  const deadline = Date.now() + timeoutMs;

  let last: ControllerHistoryRequest | null = null;
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, pollIntervalMs));
    last = await getControllerHistoryStatus(id);
    if (last.status === "completed" || last.status === "failed") {
      return last;
    }
  }

  // Timeout — surface the last known state (still pending/in_progress).
  return (
    last || {
      id,
      company_id: "",
      device_id: data.device_id,
      device_name: null,
      controllerIp: null,
      from: data.from ?? null,
      to: data.to ?? null,
      max: data.max ?? 2000,
      status: "in_progress",
      events: [],
      createdAt: "",
    }
  );
};

// ─── Access Event Sync (backfill DB from controller-log records) ─────────────
//
// Two-step, reusing the read-only history pipeline above:
//   1. fetchControllerHistoryAndWait(...) pulls the controller's stored log.
//   2. syncControllerEventsToDb(device_id, records) posts those records to the
//      cloud, which creates the events the live pipeline missed (granted +
//      denied), each entry triggering its inspection. Idempotent server-side via
//      a second-precision event_signature, so re-syncing is a no-op.

export interface HistorySyncSkipReasons {
  notAccepted: number;
  notDecision: number;
  noIdentity: number;
  duplicate: number;
  noDevice: number;
}

export interface HistorySyncSummary {
  created: number;
  skipped: number;
  entries: number;
  exits: number;
  inspections: number;
  total: number;
  skippedReasons?: HistorySyncSkipReasons;
}

export const syncControllerEventsToDb = async (
  device_id: string,
  records: ControllerHistoryRecord[],
): Promise<HistorySyncSummary> => {
  const res = await api.post("/yardAccessEventsSync", { device_id, records });
  return res.data.data;
};

// ─── Bulk delete access events (records + inspections + evidence images) ──────
// Hard delete, gated server-side to admin / admin-reseller roles. Either pass a
// list of `ids` (one, several, or every selected row) or `all: true` to wipe the
// whole company (optionally scoped to a `yard_id`) — the latter cascades to each
// event's linked inspection and that inspection's uploaded images in Storage.
export interface DeleteAccessEventsResult {
  deletedEvents: number;
  deletedInspections: number;
  all: boolean;
}

export const deleteYardAccessEvents = async (payload: {
  ids?: string[];
  all?: boolean;
  yard_id?: string;
}): Promise<DeleteAccessEventsResult> => {
  const res = await api.post("/yardAccessEvents/delete", payload);
  return res.data.data;
};

// Authoritative per-company "last DB sync" time, stamped by the backend on every
// manual/auto sync run. Returns null if the company has never been synced.
export const fetchYardSyncStatus = async (): Promise<{
  last_sync_at: string | null;
}> => {
  const res = await api.get("/yardSyncStatus");
  return res.data.data;
};

// ─── Controller Card Sync (import the device's card DB → accessCards) ─────────
//
// Same cloud-routed shape as history: the front-end creates a request, the
// on-site relay reads the controller's card database via the local SDK bridge,
// and the cloud upserts the cards into accessCards (skipping ones that already
// exist, so linked cards aren't overwritten). The front-end polls until done.

export interface ControllerCardSyncRequest {
  id: string;
  company_id: string;
  device_id: string;
  device_name: string | null;
  controllerIp: string | null;
  yard_id: string | null;
  max: number;
  status: "pending" | "in_progress" | "completed" | "failed";
  cardCount?: number;
  imported?: number;
  skipped?: number;
  errors?: number;
  truncated?: boolean;
  error?: string | null;
  createdAt: string;
  completedAt?: string | null;
}

export interface RequestCardSyncParams {
  device_id: string;
  max?: number;
}

export const requestCardSync = async (
  data: RequestCardSyncParams,
): Promise<{ id: string }> => {
  const res = await api.post("/hikvisionCardSyncRequest", data);
  return res.data.data;
};

export const getCardSyncStatus = async (
  id: string,
): Promise<ControllerCardSyncRequest> => {
  const res = await api.get("/hikvisionCardSyncRequestStatus", {
    params: { id },
  });
  return res.data.data;
};

/**
 * Create a card-sync request and poll until the relay fulfils it (or times out).
 */
export const syncCardsAndWait = async (
  data: RequestCardSyncParams,
  timeoutMs = 90000,
  pollIntervalMs = 2000,
): Promise<ControllerCardSyncRequest> => {
  const { id } = await requestCardSync(data);
  const deadline = Date.now() + timeoutMs;

  let last: ControllerCardSyncRequest | null = null;
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, pollIntervalMs));
    last = await getCardSyncStatus(id);
    if (last.status === "completed" || last.status === "failed") {
      return last;
    }
  }

  return (
    last || {
      id,
      company_id: "",
      device_id: data.device_id,
      device_name: null,
      controllerIp: null,
      yard_id: null,
      max: data.max ?? 0,
      status: "in_progress",
      createdAt: "",
    }
  );
};

// ─── Unified smart control ──────────────────────────────────────────────────
// Routes the door command to the right transport based on the device's
// `connection_mode`:
//   - "direct"     → Cloud Function calls device IP via ISAPI (synchronous).
//   - "hikgateway" → Cloud Function calls the gateway URL (synchronous).
//   - "relay"      → Writes a `doorCommand` doc and polls until the LAN
//                    relay agent picks it up and reports back.

export const controlHikvisionDeviceSmart = async (
  device: Pick<HikvisionDevice, "id" | "connection_mode">,
  action: HikvisionDoorAction,
  options: {
    door_no?: number;
    relayTimeoutMs?: number;
    // Se llama antes de cada reintento por "SDK busy" (ver doorBusyRetry.ts),
    // para que la pantalla diga que está esperando turno y no que falló.
    onBusyRetry?: (attempt: number) => void;
    // Abortarlo al desmontar: corta los reintentos para que la pluma no se
    // mueva cuando el operador ya cerró el modal o cambió de pantalla.
    signal?: AbortSignal;
  } = {},
): Promise<ControlHikvisionDeviceResult> => {
  if (device.connection_mode === "relay") {
    // The relay path only supports open/close/keepOpen. Map alwaysOpen/Close
    // to keepOpen/close as a best-effort.
    const relayAction =
      action === "alwaysOpen"
        ? "keepOpen"
        : action === "alwaysClose"
          ? "close"
          : (action as "open" | "close" | "keepOpen");

    const params = { device_id: device.id, action: relayAction, door_no: options.door_no };
    const timeoutMs = options.relayTimeoutMs ?? 15000;
    const startedAt = Date.now();
    let attempt = 1;
    let cmd = await sendDoorCommandAndWait(params, timeoutMs);
    while (
      !options.signal?.aborted &&
      shouldRetryDoorCommand({ status: cmd.status, message: cmd.result?.message }, startedAt, Date.now())
    ) {
      attempt += 1;
      options.onBusyRetry?.(attempt);
      // Pausa corta: el bridge ya esperó 8 s por el semáforo; esto sólo evita
      // martillar la cola si la búsqueda en curso todavía tiene para rato.
      await new Promise((r) => setTimeout(r, 1500));
      if (options.signal?.aborted) break;
      cmd = await sendDoorCommandAndWait(params, timeoutMs);
    }

    return {
      success: cmd.status === "completed",
      action,
      error:
        cmd.status === "completed"
          ? undefined
          : cmd.result?.message || cmd.status,
    };
  }

  return controlHikvisionDevice({
    id: device.id,
    action,
    door_no: options.door_no,
  });
};
